import { useState, useCallback, useEffect, ReactNode } from "react";
import api from "@/services/api";
import { PortalContext } from "@/pages/portal/PortalContext";
import type { Task, PortalConversation } from "@/pages/portal/PortalContext";

export function AgencyPortalProvider({ children }: { children: ReactNode }) {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [tasksLoading, setTasksLoading] = useState(true);
    const [conversations, setConversations] = useState<PortalConversation[]>([]);
    const [conversationsLoading, setConversationsLoading] = useState(true);

    const refreshTasks = useCallback(async () => {
        setTasksLoading(true);
        try {
            const res = await api.get('/integrations/hub/tasks/');
            if (res.data.status === 'success') {
                setTasks((prev) => {
                    const readSet = new Set(prev.filter((t) => t.read).map((t) => t.task_id));
                    return res.data.tasks.map((t: Task) =>
                        readSet.has(t.task_id) ? { ...t, read: true } : t
                    );
                });
            }
        } catch (error) {
            console.error('Failed to fetch tasks:', error);
        } finally {
            setTasksLoading(false);
        }
    }, []);

    const markTaskRead = useCallback(async (taskId: number, shouldComplete = false) => {
        setTasks((prev) => prev.map((t) => t.task_id === taskId ? { ...t, read: true } : t));
        try {
            if (shouldComplete) {
                await api.post(`/integrations/hub/tasks/${taskId}/complete/`);
                setTasks((prev) => prev.map((t) => t.task_id === taskId ? { ...t, read: true, status: 'Completed' } : t));
                await refreshTasks();
            }
            await api.patch(`/integrations/hub/tasks/${taskId}/read/`);
        } catch (err) {
            console.error('markTaskRead failed:', err);
            setTasks((prev) => prev.map((t) => t.task_id === taskId ? { ...t, read: false } : t));
        }
    }, [refreshTasks]);

    const refreshConversations = useCallback(async () => {
        setConversationsLoading(true);
        try {
            const res = await api.get('/integrations/hub/messages/');
            if (res.data.status === 'success') {
                setConversations((prev) => {
                    const readMsgIds = new Set<number>();
                    prev.forEach((c) => c.messages.forEach((m) => { if (m.read) readMsgIds.add(m.id); }));
                    return (res.data.conversations as PortalConversation[]).map((c) => ({
                        ...c,
                        messages: c.messages.map((m) => readMsgIds.has(m.id) ? { ...m, read: true } : m),
                    }));
                });
            }
        } catch (error) {
            console.error('Failed to fetch conversations:', error);
        } finally {
            setConversationsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshTasks();
        refreshConversations();
    }, [refreshTasks, refreshConversations]);

    const noop = () => {};

    return (
        <PortalContext.Provider value={{
            tasks,
            tasksLoading,
            conversations,
            conversationsLoading,
            applications: [],
            applicationsLoading: false,
            announces: [],
            announcesLoading: false,
            isApplying: false,
            resumeStep: 0,
            resumeAppNumber: undefined,
            pendingProgramSelection: null,
            setIsApplying: noop,
            startApplication: noop,
            startApplicationWithSelection: noop,
            clearPendingSelection: noop,
            handleComplete: noop,
            handleApplicationSubmit: noop,
            cancelApplying: noop,
            refreshTasks,
            refreshConversations,
            markTaskRead,
        }}>
            {children}
        </PortalContext.Provider>
    );
}
